import React, { useCallback, useEffect, useRef, useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { RequirementApprovalData } from "./Data/RequirementApproval.data";
import { RequirementApprovalConfig } from "./Data/RequirementApprovalConfig";
import Buttons from "../../components/ui/button/Buttons";
import FormCreator from "../../components/Forms/FormCreator";
import MolGrid from "../../components/Grid/MolGrid";
import { useGetAllRequirmentListForApprovalMutation, useLazyGetAllDropdownForCRUDCategoryQuery } from "../../app/services/Crud/crudApi";
import { setAllDropDownField } from "./Utils/dropdownUtils";
import { encryptAESID } from "../../utils/Encryption";
import DataLoader from "../../components/ui/dataLoader/DataLoader";

const DEFAULT_PAGINATION = {
    pageNo: 1,
    pageSize: 20,
    totalCount: 0,
};

const RequirementApprovalList = () => {
    const molGridRef = useRef(null);
    const requirementApprovalRef = useRef(null);
    const navigate = useNavigate();
    const [formData, setFormData] = useState(RequirementApprovalData);
    const [dataSource, setDataSource] = useState([]);
    const [paginationRequest, setPaginationRequest] = useState(DEFAULT_PAGINATION);

    const [getDropdowns, { data: dropdownData, isLoading: dropdownLoading },] = useLazyGetAllDropdownForCRUDCategoryQuery();
    const [getRequirementList, { data: requirementData, isLoading: requirementLoading },] = useGetAllRequirmentListForApprovalMutation();

    useEffect(() => {
        getDropdowns();
        fetchRequirements(DEFAULT_PAGINATION);
    }, []);

    useEffect(() => {
        if (!dropdownLoading && dropdownData) {
            setAllDropDownField(dropdownData);
        }
    }, [dropdownLoading, dropdownData]);

    useEffect(() => {
        if (!requirementLoading && requirementData) {
            setDataSource(requirementData?.itemList || []);
            setPaginationRequest((prev) => ({
                ...prev,
                totalCount: requirementData?.totalCount || 0,
            }));
        }
    }, [requirementLoading, requirementData]);

    const fetchRequirements = useCallback((request) => {
        getRequirementList(request);
    }, [getRequirementList]);

    const handleSearch = useCallback(() => {
        const formValues = requirementApprovalRef.current?.getFormData();
        if (!formValues) return;
        const request = {
            ...DEFAULT_PAGINATION,
            RequirmentName: formValues?.searchByRequirementName || null,
            ChannelId: formValues?.channel?.value || formValues?.channel || null,
            SectionId: formValues?.requirementSection?.value || formValues?.requirementSection || null,
            StatusId: formValues?.statusId?.value || formValues?.statusId || null,
        };
        setPaginationRequest(request);
        fetchRequirements(request);
    }, [fetchRequirements]);

    const handleClearClick = useCallback(() => {
        const clearedForm = {
            ...RequirementApprovalData,
            initialState: {
                searchByRequirementName: null,
                channel: null,
                statusId: null,
            },
        };

        setFormData(clearedForm);
        setPaginationRequest(DEFAULT_PAGINATION);
        fetchRequirements(DEFAULT_PAGINATION);
    }, [fetchRequirements]);

    const handlePageChange = useCallback(({ pageNumber, pageSize }) => {
        setPaginationRequest((prev) => {
            const updated = {
                ...prev,
                pageNo: pageNumber,
                pageSize,
            };
            fetchRequirements(updated);
            return updated;
        });
    }, [fetchRequirements]);

    const handleViewClick = (data) => {
        navigate(`/approveRequirement/${encryptAESID(data?.sgbpRequirementId)}`);
    }

    const actionHandler = {
        VIEW: handleViewClick,
    };

    return (
        <Suspense fallback={<DataLoader/>}>
        <div className="crud-requirement-list">
            <div className="corporate-member-list">
                <div className="sgbp-category">
                    <div className="row status-input pr-0 col-9">
                        {dropdownData &&(
                            <FormCreator ref={requirementApprovalRef} {...formData} />
                        )}
                    </div>
                    <div className="d-flex align-items-end mt-4 gap-3">
                        <Buttons
                            buttonText="Search"
                            buttonTypeClassName="search-btn "
                            onClick={handleSearch}
                        />
                        <Buttons
                            buttonText="Clear"
                            buttonTypeClassName="btn btn-secondary"
                            onClick={handleClearClick}
                        />
                    </div>
                </div>
                <div className="member-list">

                    {requirementData &&(
                        <MolGrid
                            ref={molGridRef}
                            configuration={RequirementApprovalConfig}
                            dataSource={dataSource}
                            allowPagination={true}
                            pagination={paginationRequest}
                            onPageChange={handlePageChange}
                            onActionChange={actionHandler}
                            isLoading={requirementLoading}
                        />
                    )}
                </div>
            </div>
        </div>
        </Suspense>
    );
};

export default RequirementApprovalList;
